import { Modal, Box, Button, Typography, Paper, Grid } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { useState, useEffect } from 'react'
import InfoIcon from '@mui/icons-material/Info';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';

// Custom Component
import AddEditLandRegistration from './AddEditLandRegistration';
// Custom Hooks
import useLandregistration from '../hooks/useLandregistration';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '95vw', sm: '85vw', md: '75vw' }, // ยืดหยุ่นตามขนาดหน้าจอ
    maxHeight: '90vh', // ป้องกันหน้าต่างยาวทะลุขอบจอข้ามล่าง
    overflowY: 'auto', // ถ้าข้อมูลเยอะให้เกิด Scrollbar แนวนอน/แนวตั้งแทน
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 2,
    p: 4,
    outline: 'none'
};

export default function LandRegistrationInfoModal(props) {
    const { infoModalOpen, setInfoModalOpen, rowData } = props
    const [landRegistrationData, setLandRegistrationData] = useState([])

    // Add / Edit State
    const [addEditModalOpen, setAddEditModalOpen] = useState(false)
    const [rowId, setRowId] = useState(undefined)

    const { fetchLandRegistryLog } = useLandregistration()

    // ซ่อมบั๊กการ Render คอลัมน์ตารางให้ตรงฟิลด์จริง
    const columns = [
        { field: 'id', headerName: 'ลำดับ', width: 70 },
        {
            field: 'owner',
            headerName: 'เจ้าของปัจจุบัน',
            width: 200,
            valueGetter: (params) => {
                const { new_owner_prefix, new_owner_firstname, new_owner_lastname } = params.row;
                return `${new_owner_prefix || ''}${new_owner_firstname || ''} ${new_owner_lastname || ''}`.trim();
            }
        },
        {
            field: 'previous_owner',
            headerName: 'เจ้าของเดิม',
            width: 200,
            valueGetter: (params) => {
                // 🟢 แก้ไข: ดักจับตัวแปรของฝั่งเจ้าของเดิมจริงๆ (สมมติว่าชื่อ prev_owner_...)
                const { old_owner_prefix, old_owner_firstname, old_owner_lastname } = params.row;
                return `${old_owner_prefix || ''}${old_owner_firstname || ''} ${old_owner_lastname || ''}`.trim();
            }
        },
        {
            field: 'dn',
            headerName: 'เลขที่เอกสารสิทธิ์',
            width: 150,
            valueGetter: (params) => params.row.new_dn || '-'
        },
        {
            field: 'previous_dn',
            headerName: 'เลขที่เอกสารสิทธิ์เดิม',
            width: 150,
            valueGetter: (params) => params.row.dn || '-'
        },
        {
            field: 'note',
            headerName: 'หมายเหตุ',
            width: 180,
            // 🟢 แก้ไข: เอา parseInt ออก เพื่อให้พ่นข้อความสตริงออกมาได้ปกติ ไม่ขึ้น NaN
            valueGetter: (params) => params.row.note || '-'
        },
        {
            field: 'action',
            headerName: 'การจัดการ',
            sortable: false,
            width: 150,
            renderCell: (params) => {
                return (
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<InfoIcon />}
                        onClick={() => {
                            setRowId(params.row.id); // ส่ง ID ไปแก้ไข
                            setAddEditModalOpen(true);
                        }}
                    >
                        ดู/แก้ไข
                    </Button>
                )
            }
        }
    ]

    useEffect(() => {
        if (rowData && infoModalOpen) {
            fetchLandRegistryLog(setLandRegistrationData, rowData)
        }
    }, [rowData, infoModalOpen])

    // ฟังก์ชันช่วยทำ Format ตัวเลขระวางแบบปลอดภัยแอปไม่ระเบิด
    const formatUtmMap = () => {
        if (!rowData) return '-';
        const m1 = rowData.utmmap1 ? parseInt(rowData.utmmap1) : '';
        const m2 = rowData.utmmap2 ? parseInt(rowData.utmmap2) : '';
        const m3 = rowData.utmmap3 || '';
        const m4 = rowData.utmmap4 || '';
        return `${m1}-${m2}-${m3}-${m4}`;
    };

    return (
        <Modal open={infoModalOpen} onClose={() => setInfoModalOpen(false)}>
            <Paper sx={modalStyle}>

                {/* โซนที่ 1: การ์ดหัวข้อ แสดงข้อมูลแปลงหลักที่เลือกมา */}
                <Typography variant='h6' fontWeight="bold" mb={2} color="primary">
                    🔍 รายละเอียดสารบบแปลงที่ดิน
                </Typography>

                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="textSecondary">รหัสแปลงที่ดิน:</Typography>
                            <Typography variant="body1" fontWeight="medium">{rowData?.parcel_cod || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="textSecondary">รหัสแปลงที่ดินเก่า:</Typography>
                            <Typography variant="body1" fontWeight="medium">{rowData?.old_parcel || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="textSecondary">เลขที่โฉนด (DN):</Typography>
                            <Typography variant="body1" fontWeight="medium">{rowData?.dn ? parseInt(rowData.dn) : '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="body2" color="textSecondary">หมายเลขระวางแผนที่:</Typography>
                            <Typography variant="body1" fontWeight="medium">{formatUtmMap()}</Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* โซนที่ 2: ตารางประวัติการเปลี่ยนแปลง */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant='subtitle1' fontWeight="bold">
                        📜 ประวัติการเปลี่ยนแปลงข้อมูลแปลงที่ดิน
                    </Typography>

                    <Button
                        variant='contained'
                        color='success'
                        startIcon={<AddCircleIcon />}
                        onClick={() => {
                            setRowId(undefined); // เคลียร์เป็น undefined แปลว่าเป็นการ "เพิ่มข้อมูลใหม่"
                            setAddEditModalOpen(true);
                        }}
                    >
                        เพิ่มบันทึกการเปลี่ยนแปลง
                    </Button>
                </Box>

                <div style={{ height: 350, width: '100%' }}>
                    <DataGrid
                        rows={landRegistrationData}
                        columns={columns}
                        pageSize={5}
                        rowsPerPageOptions={[5, 10, 20]}
                        disableSelectionOnClick
                    />
                </div>

                {/* ส่วนปุ่มปิดหน้าต่างใหญ่ */}
                <Box display="flex" justifyContent="flex-end" mt={3}>
                    <Button
                        variant='outlined'
                        color='inherit'
                        startIcon={<CancelIcon />}
                        onClick={() => setInfoModalOpen(false)}
                    >
                        ปิดหน้าต่าง
                    </Button>
                </Box>

                {/* ในไฟล์ LandRegistrationInfoModal.js ใกล้ๆ บรรทัดล่างสุด */}
                <AddEditLandRegistration
                    rowId={rowId}
                    addEditModalOpen={addEditModalOpen}
                    setAddEditModalOpen={setAddEditModalOpen}
                    parentParcelCode={rowData?.parcel_cod} // 🟢 ส่งรหัสแปลงแม่ไปล็อคในฟอร์มย่อย
                    refreshData={() => fetchLandRegistryLog(setLandRegistrationData, rowData)} // 🟢 ส่งฟังก์ชันไปสั่งโหลดตารางใหม่ตอนกดเซฟเสร็จ
                />
            </Paper>
        </Modal>
    )
}